/**
 * TanStack Query bindings.
 *
 * Split from `client.ts` so a non-React consumer — a script, a server-side render, a worker — can
 * import the plain client without pulling React and the query cache into its bundle.
 *
 * The `/v5` entry point is the one that matches @tanstack/react-query v5; the package's default
 * export still targets v4 and its hooks have a different signature.
 */

import { initTsrReactQuery } from '@ts-rest/react-query/v5';
import type { ClientArgs } from '@ts-rest/core';
import { contract } from '@hrms/shared';
import { createClientArgs, type ApiClientOptions } from './client.js';

/**
 * Named explicitly against the general `ClientArgs` interface rather than
 * `ReturnType<typeof createClientArgs>` (the precise, literal-typed object `createClientArgs`
 * builds) — and `createApiQueryClient` below is annotated with it rather than left to infer.
 *
 * With neither of those, TypeScript has to compute and print the full structural type of
 * `initTsrReactQuery(contract, ...)` — every route's every hook variant, recursively, across the
 * whole merged contract — twice: once here as a type alias, once again at every call site that
 * assigns the result to something exported. Once the recruitment/careers routers joined the
 * contract, that structural type became too large to serialize and both `tsc` invocations failed
 * with TS7056. Naming the type via `ClientArgs` (a small, already-exported interface) and giving
 * the function an explicit return type means the compiler only ever needs to print `ApiQueryClient`
 * by name, never expand it — the same fix `apps/web/src/lib/api.ts`'s call site applied locally;
 * this is the same fix at the source, so every OTHER consumer (apps/mobile included) needs no
 * workaround of its own.
 */
export type ApiQueryClient = ReturnType<typeof initTsrReactQuery<typeof contract, ClientArgs>>;

/**
 * Built from the same `createClientArgs` as the plain client, so the bearer header and the
 * single-flight refresh apply identically whether a call came from a hook or from imperative code.
 * Two separately configured clients is how an app ends up refreshing twice.
 */
export function createApiQueryClient(options: ApiClientOptions): ApiQueryClient {
  return initTsrReactQuery(contract, createClientArgs(options));
}
